import { useState, type FormEvent } from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, CheckCircle2, AlertCircle, Lock } from 'lucide-react';

/**
 * Demo request form.
 *
 * Posts to /api/lead, which is a Cloudflare Pages Function living beside this
 * site — deliberately not an endpoint on intent360-core. The marketing site is
 * public and unauthenticated; coupling it to the application API would mean
 * exposing a writable core endpoint to the open internet, and core has no
 * authentication layer yet.
 *
 * If the endpoint is not configured, the form degrades to a mailto link rather
 * than silently swallowing a lead.
 */

const CONTACT_EMAIL = 'hello@intent360.co.in';

type State =
  | { kind: 'idle' }
  | { kind: 'sending' }
  | { kind: 'sent' }
  | { kind: 'error'; message: string };

export function DemoForm() {
  const [state, setState] = useState<State>({ kind: 'idle' });

  async function submit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const form = e.currentTarget;
    const data = Object.fromEntries(new FormData(form)) as Record<string, string>;

    // Honeypot. A real person never sees this field, so anything in it is a bot.
    // Report success rather than an error — telling a scraper it was detected
    // just teaches it to try again without the field.
    if (data.company_website) {
      setState({ kind: 'sent' });
      return;
    }

    setState({ kind: 'sending' });
    try {
      const res = await fetch('/api/lead', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: data.name,
          email: data.email,
          company: data.company,
          message: data.message,
        }),
      });

      if (!res.ok) {
        // The function returns {error} on every failure path; fall back to the
        // status code if the body is missing or not JSON.
        const body = (await res.json().catch(() => null)) as { error?: string } | null;
        throw new Error(body?.error ?? `Request failed (${res.status})`);
      }

      form.reset();
      setState({ kind: 'sent' });
    } catch (err) {
      setState({
        kind: 'error',
        message: err instanceof Error ? err.message : 'Something went wrong.',
      });
    }
  }

  if (state.kind === 'sent') {
    return (
      <motion.div
        className="alert alert--ok"
        role="status"
        initial={{ opacity: 0, scale: 0.96 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
      >
        <CheckCircle2 size={18} />
        <span>Thanks — we have your details and will be in touch within one working day.</span>
      </motion.div>
    );
  }

  return (
    <form className="form" onSubmit={submit}>
      <div className="field">
        <label htmlFor="name">Name</label>
        <input id="name" name="name" required autoComplete="name" />
      </div>

      <div className="field">
        <label htmlFor="email">Work email</label>
        <input id="email" name="email" type="email" required autoComplete="email" />
      </div>

      <div className="field">
        <label htmlFor="company">Company</label>
        <input id="company" name="company" required autoComplete="organization" />
      </div>

      <div className="field">
        <label htmlFor="message">What are you trying to measure? <span>(optional)</span></label>
        <textarea id="message" name="message" />
      </div>

      {/* Honeypot — hidden from people and from screen readers alike. */}
      <div className="hp" aria-hidden="true">
        <label htmlFor="company_website">Do not fill this in</label>
        <input id="company_website" name="company_website" tabIndex={-1} autoComplete="off" />
      </div>

      {state.kind === 'error' && (
        <div className="alert alert--bad" role="alert">
          <AlertCircle size={18} />
          <span>
            {state.message} You can also email us directly at{' '}
            <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>.
          </span>
        </div>
      )}

      <button
        className="btn btn--primary btn--lg"
        type="submit"
        disabled={state.kind === 'sending'}
        style={{ width: '100%', justifyContent: 'center' }}
      >
        {state.kind === 'sending' ? 'Sending…' : <>Request a demo <ArrowRight size={17} /></>}
      </button>

      <p className="formnote">
        <Lock size={13} /> We use this to contact you about Intent360. No newsletter, no list rental.
      </p>
    </form>
  );
}
